import { AlertCircle, Check, Info, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { PipelineStageTimeline } from '~/components/common/pipeline-stage-timeline/pipeline-stage-timeline';
import { useScheduledDeliveryStatus } from '~/features/content-enrichment/hooks/use-scheduled-trigger-status';

interface Props {
  /** The `scheduled-job-…` slug (`BatchHeader.batchId`) — same id family the
   *  Enrichment Overview funnel's own Delivery step already polls. */
  jobId: string;
  /** Fires once, on the transition into a completed delivery, so the host can
   *  navigate to batch details. */
  onComplete: () => void;
  /** Root test id — overridden when nested inside the admin run step so the panel id stays unique. */
  testId?: string;
}

/**
 * Step 3 of the delivery wizard — live delivery progress. Polls the same
 * `GET /enrichments/scheduled/jobs/{jobId}/status` endpoint (adaptive
 * backoff, stops on terminal) the existing scheduled-delivery page already
 * uses, rendering the shared `PipelineStageTimeline`.
 */
export function DeliveryWizardProgressStep({
  jobId,
  onComplete,
  testId = 'delivery-wizard-step-panel-progress',
}: Props) {
  const { t } = useTranslation('common');
  const { stages, isDeliveryComplete, isFailed } = useScheduledDeliveryStatus(jobId);

  useEffect(() => {
    if (isDeliveryComplete) onComplete();
  }, [isDeliveryComplete, onComplete]);

  return (
    <div
      className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center gap-6 py-10"
      data-testid={testId}
    >
      {isFailed ? (
        <span
          className="bg-destructive flex h-16 w-16 items-center justify-center rounded-full"
          data-testid="delivery-wizard-progress-failed-icon"
        >
          <AlertCircle aria-hidden="true" className="h-8 w-8 text-white" strokeWidth={2.5} />
        </span>
      ) : isDeliveryComplete ? (
        <span
          className="bg-success flex h-16 w-16 items-center justify-center rounded-full"
          data-testid="delivery-wizard-progress-complete-icon"
        >
          <Check aria-hidden="true" className="h-8 w-8 text-white" strokeWidth={2.5} />
        </span>
      ) : (
        <Loader2
          aria-hidden="true"
          className="text-primary h-16 w-16 animate-spin"
          strokeWidth={1.5}
          data-testid="delivery-wizard-progress-loading"
        />
      )}

      <div className="text-center">
        <h2
          className="text-foreground text-xl font-bold"
          data-testid="delivery-wizard-progress-title"
        >
          {isFailed
            ? t('deliveryWizard.progress.failed.title')
            : isDeliveryComplete
              ? t('deliveryWizard.progress.complete.title')
              : t('deliveryWizard.progress.inProgress.title')}
        </h2>
        <p
          className="text-muted-foreground mx-auto mt-2 max-w-md text-sm"
          data-testid="delivery-wizard-progress-body"
        >
          {isFailed
            ? t('deliveryWizard.progress.failed.body')
            : isDeliveryComplete
              ? t('deliveryWizard.progress.complete.body')
              : t('deliveryWizard.progress.inProgress.body')}
        </p>
      </div>

      {stages.length > 0 && <PipelineStageTimeline stages={stages} showInProgressBadge showCount />}

      {!isFailed && !isDeliveryComplete && (
        <div
          className="bg-info-50 text-info flex w-full max-w-xl items-center gap-2 rounded-lg px-4 py-3 text-sm"
          data-testid="delivery-wizard-progress-safe-to-leave-banner"
        >
          <Info aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span>{t('deliveryWizard.progress.safeToLeave')}</span>
        </div>
      )}
    </div>
  );
}
